import axios from 'axios';
import { call, put, select } from 'redux-saga/effects';
import { API } from '../constants/api';
import { methods } from '../constants/constants';
import { actions } from '../reducers/examenes';
import { actions as preguntasActions } from '../reducers/preguntas';
import { registerPendingExamUpload } from '../utils/examUploadCleanup';
import { sagaNames, handleSagaError } from './index';
import { normalizeExamenesAcademiaUsuario } from './normalizers/examenesNormalizer';


// Gets examenesAlreadyFetched
export const examenesFromStore = state => state.examenes.examenes;
export const examenesAcademiaUsuarioFromStore = state => state.examenes.examenesAcademiaUsuario;
export const checkExamenGuardadoFromStore = state => state.examenes.checkExamenGuardado;
export const examenGuardadoFromStore = state => state.examenes.examenGuardado;

function fetchExamenes() {
	return axios({
		method: methods.GET,
		url: API.EXAMENES.EXAMENES,
	});
}

function postExamenesData(examen) {
	return axios({
		method: methods.POST,
		url: API.EXAMENES.EXAMENES,
    data: {
      nombre: examen.nombre,
      plataforma: examen.plataforma,
    },
	});
}

function putExamenesData(examen) {
	return axios({
		method: methods.PUT,
		url: API.EXAMENES.EXAMENES,
    data: {
      id: examen.id,
      nombre: examen.nombre,
      plataforma: examen.plataforma,
    },
	});
}

function deleteExamenesData(examen) {
	return axios({
		method: methods.DELETE,
    url: API.EXAMENES.EXAMENES,
    data: {
      id: examen.id,
      id_academia: examen.id_academia,
      nombre: examen.nombre,
      visible: examen.visible,
    }
	});
}

function putUpdateExamen(examen) {
	return axios({
		method: methods.PUT,
    url: API.EXAMENES.UPDATE_EXAMEN,
    data: {
      idExamen: examen.id,
      visible: examen.visible,
      nombreExamen: examen.nombreExamen,
    }
	});
}

function fetchVisibilidadExterna(idExamen) {
  return axios({
    method: methods.GET,
    url: `${API.EXAMENES.GET_VISIBILIDAD_EXTERNA}?idExamen=${idExamen}`,
  });
}

function putUpdateVisibilidadExterna(payload) {
  return axios({
    method: methods.PUT,
    url: API.EXAMENES.UPDATE_VISIBILIDAD_EXTERNA,
    data: payload,
  });
}

function subirExamenesData(examen) {
	return axios({
		method: methods.POST,
		url: API.FILE.UPLOAD_FILE,
    data: examen,
	});
}

function isConnectionUploadError(error) {
  return !error?.response;
}

function fetchExamenesAcademiaUsuario() {
	return axios({
		method: methods.GET,
		url: API.EXAMENES.GET_EXAMENES_ACADEMIA_USUARIO,
	});
}

function fetchCheckExamenGuardado() {
	return axios({
		method: methods.GET,
		url: API.EXAMENES.CHECK_EXAMEN_GUARDADO,
	});
}

function fetchExamenGuardado() {
	return axios({
		method: methods.GET,
		url: API.EXAMENES.GET_EXAMEN_GUARDADO,
	});
}

function postGuardarExamenData(examen, isErroneas, modo, nombre = 'examenGuardado') {
	return axios({
    method: methods.POST,
    url: API.EXAMENES.POST_GUARDAR_EXAMEN,
    data: {
      listaPreguntas: examen,
      isErroneas, 
      modo,
      nombre
    }
	});
}

function deleteExamenGuardadoData() {
	return axios({
		method: methods.DELETE,
    url: API.EXAMENES.ELIMINAR_EXAMEN_GUARDADO,
	});
}

export function* getExamenes(args) {
  try {
    const examenesStore = yield select(examenesFromStore);
    let examenes = null;
    const forceRefresh = !!args?.options?.force;

    if (forceRefresh || !examenesStore || (Array.isArray(examenesStore) && examenesStore.length === 0)) {
      const response = yield call(fetchExamenes);
      examenes = response && response.data;
    } else {
      examenes = examenesStore;
    }
    yield put(actions.successGetExamenes(examenes));
  } catch (error) {
    const reducerFailureAction = actions.errorGetExamenes(error);
    yield handleSagaError(sagaNames.GET_EXAMENES, error, reducerFailureAction);
  }
}

export function* postExamenes(args) {
    try {
    const response = yield call(postExamenesData, args.examen);
    // const status = response && response.status;
    yield put(actions.successPostExamenes(response));
  } catch (error) {
    const reducerFailureAction = actions.errorPostExamenes(error);
    yield handleSagaError(sagaNames.POST_EXAMENES, error, reducerFailureAction);
  }
}

export function* editExamenes(args) {
  try {
    const response = yield call(putExamenesData, args.examen);
    // const status = response && response.status;
    yield put(actions.successEditExamenes(response));
  } catch (error) {
    const reducerFailureAction = actions.errorEditExamenes(error);
    yield handleSagaError(sagaNames.EDIT_EXAMENES, error, reducerFailureAction);
  }
}

export function* deleteExamenes(args) {
  try {
    let response = null;

    if (Array.isArray(args.examen)) {
      const deletedExamenes = [];
      const failedExamenes = [];

      for (const examen of args.examen) {
        try {
          const currentResponse = yield call(deleteExamenesData, examen);

          if (currentResponse?.data) {
            deletedExamenes.push(currentResponse.data);
          } else {
            failedExamenes.push({
              id: examen?.id,
              nombre: examen?.nombre,
              message: 'No se pudo borrar el examen seleccionado.',
            });
          }
        } catch (error) {
          const responseData = error?.response?.data;
          failedExamenes.push({
            id: examen?.id,
            nombre: examen?.nombre,
            message: typeof responseData === 'string'
              ? responseData
              : responseData?.message || error?.message || 'No se pudo borrar el examen seleccionado.',
            status: error?.response?.status || error?.status || null,
          });
        }
      }

      if (deletedExamenes.length === 0 && failedExamenes.length > 0) {
        const firstFailure = failedExamenes[0];
        const batchError = new Error(firstFailure?.message || 'No se pudieron borrar los exámenes seleccionados.');

        batchError.response = {
          status: firstFailure?.status || 500,
          data: firstFailure?.message || 'No se pudieron borrar los exámenes seleccionados.',
        };

        throw batchError;
      }

      response = {
        status: failedExamenes.length > 0 ? 207 : 200,
        data: deletedExamenes,
        meta: {
          ...(args.options || {}),
          failedExamenes,
        },
      };
    } else {
      const currentResponse = yield call(deleteExamenesData, args.examen);
      response = {
        ...currentResponse,
        meta: args.options || null,
      };
    }

    yield put(actions.successDeleteExamenes(response));
  } catch (error) {
    const reducerFailureAction = actions.errorDeleteExamenes(error);
    yield handleSagaError(sagaNames.DELETE_EXAMENES, error, reducerFailureAction);
  }
}

export function* postSubirExamen(args) {
  try {
  const response = yield call(subirExamenesData, args.examen);
  registerPendingExamUpload(response?.data?.examen, typeof args.examen?.get === 'function' ? args.examen.get('id_usuario') : null);
  yield put(actions.successPostSubirExamen(response));
} catch (error) {
  const reducerFailureAction = actions.errorPostSubirExamen(error);
  yield handleSagaError(sagaNames.POST_SUBIR_EXAMEN, error, reducerFailureAction);
}
}

export function* postSubirExamenesBatch(args) {
  try {
    const total = Array.isArray(args.examenes) ? args.examenes.length : 0;
    const uploadedExamenes = [];
    let batchAborted = false;

    for (let index = 0; index < total; index += 1) {
      const currentExamen = args.examenes[index];
      const currentFileName = currentExamen?.fileName || `Examen ${index + 1}`;

      yield put(actions.progressPostSubirExamenesBatch({
        total,
        completed: index,
        current: index + 1,
        currentFileName,
      }));

      try {
        const response = yield call(subirExamenesData, currentExamen.formData);
        registerPendingExamUpload(response?.data?.examen, currentExamen?.userId || (typeof currentExamen?.formData?.get === 'function' ? currentExamen.formData.get('id_usuario') : null));
        uploadedExamenes.push({
          fileName: currentFileName,
          examName: currentExamen?.examName || currentFileName,
          success: true,
          data: response?.data,
        });
      } catch (error) {
        const status = error?.status || error?.response?.status;

        if (status === 498 || status === 401) {
          const reducerFailureAction = actions.errorPostSubirExamenesBatch(error);
          yield handleSagaError(sagaNames.POST_SUBIR_EXAMENES_BATCH, error, reducerFailureAction);
          return;
        }

        if (isConnectionUploadError(error)) {
          batchAborted = true;
          uploadedExamenes.push({
            fileName: currentFileName,
            examName: currentExamen?.examName || currentFileName,
            success: false,
            error,
          });
          break;
        }

        uploadedExamenes.push({
          fileName: currentFileName,
          examName: currentExamen?.examName || currentFileName,
          success: false,
          error,
        });
      }
    }

    yield put(actions.successPostSubirExamenesBatch({
      status: 200,
      data: uploadedExamenes,
      progress: {
        total,
        completed: total,
        current: total,
        currentFileName: total > 0 ? uploadedExamenes[total - 1]?.fileName || '' : '',
      },
      meta: {
        aborted: batchAborted,
      },
    }));
  } catch (error) {
    const reducerFailureAction = actions.errorPostSubirExamenesBatch(error);
    yield handleSagaError(sagaNames.POST_SUBIR_EXAMENES_BATCH, error, reducerFailureAction);
  }
}

export function* getExamenesAcademiaUsuario() {
  try {
    const examenesAcademiaUsuarioStore = yield select(examenesAcademiaUsuarioFromStore);
    let examenes = null;
    if (!examenesAcademiaUsuarioStore || (Array.isArray(examenesAcademiaUsuarioStore) && examenesAcademiaUsuarioStore.length === 0)) {
      const response = yield call(fetchExamenesAcademiaUsuario);
      examenes = response && response.data;
    } else {
      examenes = examenesAcademiaUsuarioStore;
    }
    const examenesNormalizer = normalizeExamenesAcademiaUsuario(examenes)
    yield put(actions.successGetExamenesAcademiaUsuario(examenesNormalizer));
  } catch (error) {
    const reducerFailureAction = actions.errorGetExamenesAcademiaUsuario(error);
    yield handleSagaError(sagaNames.GET_EXAMENES_ACADEMIA_USUARIO, error, reducerFailureAction);
  }
}

export function* updateExamen(args) {
  try {
    const response = yield call(putUpdateExamen, args.examen);
    // const status = response && response.status;
    yield put(actions.successUpdateExamen(response));
    yield put(preguntasActions.successUpdatePreguntasExamen(response));
  } catch (error) {
    const reducerFailureAction = actions.errorUpdateExamen(error);
    yield handleSagaError(sagaNames.UPDATE_EXAMEN, error, reducerFailureAction);
  }
}

export function* getVisibilidadExterna(args) {
  try {
    const response = yield call(fetchVisibilidadExterna, args.idExamen);
    yield put(actions.successGetVisibilidadExterna(response.data));
  } catch (error) {
    const reducerFailureAction = actions.errorGetVisibilidadExterna(error);
    yield handleSagaError(sagaNames.GET_VISIBILIDAD_EXTERNA, error, reducerFailureAction);
  }
}

export function* batchGetVisibilidadExterna(args) {
  try {
    const idsExamenes = Array.isArray(args?.idsExamenes)
      ? Array.from(new Set(
        args.idsExamenes
          .map((idExamen) => Number(idExamen))
          .filter((idExamen) => Number.isFinite(idExamen))
      ))
      : [];

    if (idsExamenes.length === 0) {
      yield put(actions.successBatchGetVisibilidadExterna({ status: 200, data: [] }));
      return;
    }

    const visibilidades = [];

    for (const idExamen of idsExamenes) {
      const response = yield call(fetchVisibilidadExterna, idExamen);
      visibilidades.push(response?.data);
    }

    yield put(actions.successBatchGetVisibilidadExterna({
      status: 200,
      data: visibilidades,
    }));
  } catch (error) {
    const reducerFailureAction = actions.errorBatchGetVisibilidadExterna(error);
    yield handleSagaError(sagaNames.GET_VISIBILIDAD_EXTERNA, error, reducerFailureAction);
  }
}

export function* updateVisibilidadExterna(args) {
  try {
    const response = yield call(putUpdateVisibilidadExterna, args.payload);
    yield put(actions.successUpdateVisibilidadExterna(response));
  } catch (error) {
    const reducerFailureAction = actions.errorUpdateVisibilidadExterna(error);
    yield handleSagaError(sagaNames.UPDATE_VISIBILIDAD_EXTERNA, error, reducerFailureAction);
  }
}

export function* batchUpdateVisibilidadExterna(args) {
  try {
    const idsExamenes = Array.isArray(args?.payload?.idsExamenes)
      ? Array.from(new Set(
        args.payload.idsExamenes
          .map((idExamen) => Number(idExamen))
          .filter((idExamen) => Number.isFinite(idExamen))
      ))
      : [];
    const idsUsuarios = Array.isArray(args?.payload?.idsUsuarios)
      ? Array.from(new Set(
        args.payload.idsUsuarios
          .map((idUsuario) => Number(idUsuario))
          .filter((idUsuario) => Number.isFinite(idUsuario))
      ))
      : [];

    if (idsExamenes.length === 0) {
      yield put(actions.successBatchUpdateVisibilidadExterna({ status: 200, data: [] }));
      return;
    }

    const updatedExamenes = [];

    for (const idExamen of idsExamenes) {
      const response = yield call(putUpdateVisibilidadExterna, {
        idExamen,
        idsUsuarios,
      });

      updatedExamenes.push(response?.data);
    }

    yield put(actions.successBatchUpdateVisibilidadExterna({
      status: 200,
      data: updatedExamenes,
    }));
  } catch (error) {
    const reducerFailureAction = actions.errorBatchUpdateVisibilidadExterna(error);
    yield handleSagaError(sagaNames.UPDATE_VISIBILIDAD_EXTERNA, error, reducerFailureAction);
  }
}

export function* checkExamenGuardado() {
  try {
    const response = yield call(fetchCheckExamenGuardado);
    yield put(actions.successCheckExamenGuardado(response.data));
  } catch (error) {
    const reducerFailureAction = actions.errorCheckExamenGuardado(error);
    yield handleSagaError(sagaNames.CHECK_EXAMEN_GUARDADO, error, reducerFailureAction);
  }
}

export function* getExamenGuardado() {
  try {
    const examenGuardadoStore = yield select(examenGuardadoFromStore);
    let examenGuardado = null;
    if (!examenGuardadoStore) {
      const response = yield call(fetchExamenGuardado);
      examenGuardado = response && response.data;
    } else {
      examenGuardado = examenGuardadoStore;
    }
    yield put(actions.successGetExamenGuardado(examenGuardado));
  } catch (error) {
    const reducerFailureAction = actions.errorGetExamenGuardado(error);
    yield handleSagaError(sagaNames.GET_EXAMEN_GUARDADO, error, reducerFailureAction);
  }
}

export function* postGuardarExamen(args) {
  const { examen, isErroneas, modo, nombre } = args;
  try {
    let response = null;
    let examenResponse = null;
    response = yield call(postGuardarExamenData, examen, isErroneas, modo, nombre);
    examenResponse = response && response.data;
    yield put(actions.successPostGuardarExamen(examenResponse));
  } catch (error) {
    const reducerFailureAction = actions.errorPostGuardarExamen(error);
    yield handleSagaError(sagaNames.POST_GUARDAR_EXAMEN, error, reducerFailureAction);
  }
}

export function* eliminarExamenGuardado() {
  try {
    const response = yield call(deleteExamenGuardadoData);
    yield put(actions.successEliminarExamenGuardado(response));
  } catch (error) {
    const reducerFailureAction = actions.errorEliminarExamenGuardado(error);
    yield handleSagaError(sagaNames.ELIMINAR_EXAMEN_GUARDADO, error, reducerFailureAction);
  }
}
